// Admin console Overview (reset PR7; DS ui_kits/desktop/AdminScreen.jsx, artboard 1i): the facts
// an admin checks first, each read from an endpoint the other admin pages already use — usage
// (GET /orgs/{id}/usage), SSO (GET /orgs/{id}/sso), AI slots (F4 /transcription + F1 llm
// providers), the audit log and, since F13a, the quota meters (GET /orgs/{id}/quotas, FR-O9/O10:
// storage, members, 30-day public bandwidth, hosted AI minutes against the org's limits). Each card loads on its own, so one failing endpoint never blanks the page.
//
// Layout: one screen, no scrolling at laptop size. An attention strip (only when something is
// broken or near a limit), stat tiles with each limit's meter folded in, one row of short status
// cards, and a tabbed panel (Recent audit events | Limits) for the parts that grow.
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Badge, Callout, Card, Icon, Meter, Tabs } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type {
  AskSettingsOut,
  AuditEntryOut,
  LastNotesErrorOut,
  ListResponse,
  LlmProvidersList,
  OrgQuotaMetersOut,
  OrgUsageOut,
  QuotaMeterOut,
  SsoConfigOut,
  TranscriptionSettingsOut,
} from "../api/types";
import { formatBytes, formatRelativeTime } from "../library/format";
import { adminPath } from "../library/routes";
import { eventLabel } from "./auditEvents";
import { Button } from "../components/ui";
import type { AdminOutletContext } from "./AdminLayout";
import { useIsPlatformAdmin } from "../ops/platformAdmin";
import { aiFailureFlags } from "./AiSettingsPage";

type Loaded<T> = { data: T } | { error: string } | null;

function useLoaded<T>(path: string, failure: string): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>(null);
  useEffect(() => {
    let live = true;
    api<T>("GET", path)
      .then((data) => live && setState({ data }))
      .catch((err) => live && setState({ error: err instanceof ApiFail ? err.message : failure }));
    return () => {
      live = false;
    };
  }, [path, failure]);
  return state;
}

function Failed({ state }: { state: { error: string } }) {
  return (
    <Callout tone="danger" icon="warning-circle">
      {state.error}
    </Callout>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="admin-overview__row">
      <span>{label}</span>
      {children}
    </div>
  );
}

const MINUTE = 60;

function meterRow(label: string, used: number, limit: number | null, show: (n: number) => string) {
  if (limit == null) {
    return <Meter key={label} label={label} valueLabel={`${show(used)} · no limit`} value={0} />;
  }
  const ratio = limit > 0 ? used / limit : 1;
  return (
    <Meter
      key={label}
      label={label}
      valueLabel={`${show(used)} of ${show(limit)} · ${Math.round(ratio * 100)}%`}
      value={ratio}
      tone={ratio >= 1 ? "now" : "accent"}
    />
  );
}

/** A quota is "near" its limit from this share of it. A constant, not a setting (SD58): making it
 * adjustable would be an admin-console row, not an env var. */
export const NEAR_LIMIT = 0.8;

type QuotaKey = "storage_bytes" | "members" | "public_egress_bytes_30d" | "hosted_stt_seconds_30d";

const count = (n: number) => String(n);
const minutes = (s: number) => `${Math.round(s / MINUTE)} min`;

const QUOTAS: { key: QuotaKey; label: string; show: (n: number) => string; atLimit: string; blocks: boolean }[] = [
  { key: "storage_bytes", label: "Storage", show: formatBytes, atLimit: "New uploads are blocked.", blocks: true },
  { key: "members", label: "Members", show: count, atLimit: "New invites are blocked.", blocks: true },
  {
    key: "public_egress_bytes_30d",
    label: "Public bandwidth, last 30 days",
    show: formatBytes,
    // FR-ST23: a bandwidth cap warns and keeps serving; playback only stops at the hard ceiling.
    atLimit: "Public playback continues, and admins are notified.",
    blocks: false,
  },
  {
    key: "hosted_stt_seconds_30d",
    label: "Hosted AI minutes, last 30 days",
    show: minutes,
    atLimit: "Hosted transcription is blocked until usage drops.",
    blocks: true,
  },
];

/** One item in the attention strip. `broken` = not working now (red); `near` = still working but
 * heading for a limit, or a one-off failure worth a look (amber). Each kind has its own icon and
 * wording, never colour alone. */
export interface AttentionItem {
  id: string;
  kind: "broken" | "near";
  message: string;
  /** In-app link to the fix, or the Limits tab on this page. */
  to: string | "limits";
  /** Button label; "View details" unless the link goes somewhere more specific. */
  action?: string;
}

/** FR-O10: quota flags. A limit reached blocks (except bandwidth, FR-ST23); at NEAR_LIMIT or more
 * it is a warning. No limit set, no flag. */
export function quotaAttention(q: OrgQuotaMetersOut): AttentionItem[] {
  const items: AttentionItem[] = [];
  for (const meta of QUOTAS) {
    const meter: QuotaMeterOut = q[meta.key];
    if (meter.limit == null || meter.limit <= 0) continue;
    const ratio = meter.used / meter.limit;
    const amounts = `${meta.show(meter.used)} of ${meta.show(meter.limit)}`;
    if (ratio >= 1) {
      items.push({
        id: `quota-${meta.key}`,
        kind: meta.blocks ? "broken" : "near",
        message: `${meta.label} ${meta.key === "members" ? "are" : "is"} ${ratio > 1 ? "over" : "at"} the limit (${amounts}). ${meta.atLimit}`,
        to: "limits",
      });
    } else if (ratio >= NEAR_LIMIT) {
      items.push({
        id: `quota-${meta.key}`,
        kind: "near",
        message: `${meta.label} ${meta.key === "members" ? "are" : "is"} at ${Math.round(ratio * 100)}% of the limit (${amounts}).`,
        to: "limits",
      });
    }
  }
  return items;
}

/** Broken first, then near; within a kind, the order they were raised. */
export function sortAttention(items: AttentionItem[]): AttentionItem[] {
  return [...items.filter((i) => i.kind === "broken"), ...items.filter((i) => i.kind === "near")];
}

/** FR-O10: quotas, not invoices. Over a limit blocks new uploads/invites; nothing is deleted. */
function LimitsDetail({ q }: { q: Loaded<OrgQuotaMetersOut> }) {
  if (!q) return <span className="gk-sm">Loading…</span>;
  if ("error" in q) return <Failed state={q} />;
  return (
    <div className="admin-quota">
      {QUOTAS.map((meta) => meterRow(meta.label, q.data[meta.key].used, q.data[meta.key].limit, meta.show))}
      <span className="gk-caption">
        Members watching are not counted against public bandwidth. Going over a limit blocks new uploads or invites;
        existing recordings keep playing.
      </span>
    </div>
  );
}

/** A headline number with its limit folded in: the value, then "of {limit}" and a thin meter. */
function Tile({
  label,
  value,
  meter,
  show,
  caption,
}: {
  label: string;
  value: ReactNode;
  meter?: QuotaMeterOut | null;
  show?: (n: number) => string;
  caption?: ReactNode;
}) {
  const limited = meter && meter.limit != null && meter.limit > 0 && show;
  const ratio = limited ? meter.used / (meter.limit as number) : 0;
  return (
    <Card size="sm" className="admin-stat admin-tile">
      <span className="gk-sm">{label}</span>
      <span className="gk-h2 admin-stat__value">{value}</span>
      {limited ? (
        <>
          <Meter value={Math.min(ratio, 1)} tone={ratio >= NEAR_LIMIT ? "now" : "accent"} />
          <span className="gk-caption">
            of {show(meter.limit as number)} · {Math.round(ratio * 100)}%
          </span>
        </>
      ) : meter ? (
        <span className="gk-caption">No limit</span>
      ) : null}
      {caption ? <span className="gk-caption">{caption}</span> : null}
    </Card>
  );
}

function SsoCard({ orgId, slug }: { orgId: string; slug: string }) {
  const sso = useLoaded<SsoConfigOut>(`/orgs/${orgId}/sso`, "Couldn't load SSO status.");
  return (
    <Card size="sm" title="Single sign-on" action={<Link to={adminPath(slug, "sso")}>Manage</Link>}>
      {!sso ? (
        <span className="gk-sm">Loading…</span>
      ) : "error" in sso ? (
        <Failed state={sso} />
      ) : (
        <>
          <Row label="OIDC">
            {sso.data.configured ? (
              <Badge tone="success" size="sm" dot>
                Connected
              </Badge>
            ) : (
              <Badge size="sm">Not configured</Badge>
            )}
          </Row>
          {sso.data.configured ? (
            <>
              <Row label="Required for all members">
                <span className="gk-sm">{sso.data.enforced ? "Yes" : "No"}</span>
              </Row>
              <Row label="Identity provider">
                <span className="gk-sm gk-mono">{sso.data.issuer_url ?? "—"}</span>
              </Row>
            </>
          ) : (
            <span className="gk-caption">Members sign in with email and password or Google / Microsoft.</span>
          )}
        </>
      )}
    </Card>
  );
}

const SLOT_NAME = { stt: "Speech-to-text", diarization: "Speaker labels" } as const;

function slotBadge(connected: boolean, enabled = true) {
  if (!enabled) return <Badge size="sm" icon="pause">Turned off</Badge>;
  return connected ? (
    <Badge tone="success" size="sm" dot>
      Connected
    </Badge>
  ) : (
    <Badge size="sm">Not connected</Badge>
  );
}

/** `stt` comes from the page, which also reads it for the attention strip. */
function AiCard({ orgId, slug, stt }: { orgId: string; slug: string; stt: Loaded<TranscriptionSettingsOut> }) {
  const llm = useLoaded<LlmProvidersList>(`/orgs/${orgId}/transcription/providers?slot=llm`, "Couldn't load the language model.");
  return (
    <Card size="sm" title="AI and models" action={<Link to={adminPath(slug, "ai")}>Manage</Link>}>
      {!stt ? (
        <span className="gk-sm">Loading…</span>
      ) : "error" in stt ? (
        <Failed state={stt} />
      ) : (
        stt.data.slots.map((s) => (
          <Row key={s.slot} label={SLOT_NAME[s.slot as keyof typeof SLOT_NAME] ?? s.slot}>
            {slotBadge(s.status === "available", s.enabled)}
          </Row>
        ))
      )}
      <Row label="Language model">
        {!llm ? (
          <span className="gk-sm">Loading…</span>
        ) : "error" in llm ? (
          <Badge size="sm">Unknown</Badge>
        ) : (
          slotBadge(llm.data.items.some((p) => p.is_active))
        )}
      </Row>
    </Card>
  );
}

function AuditList({ audit }: { audit: Loaded<ListResponse<AuditEntryOut>> }) {
  if (!audit) return <span className="gk-sm">Loading…</span>;
  if ("error" in audit) return <Failed state={audit} />;
  if (audit.data.items.length === 0) return <span className="gk-sm">No events yet.</span>;
  return (
    <ul className="admin-overview__events">
      {audit.data.items.slice(0, AUDIT_ROWS).map((e) => (
        <li key={e.id}>
          <Icon name="clock-counter-clockwise" size={18} />
          <span className="admin-overview__event">
            <b>{eventLabel(e.event)}</b> · {e.actor_email}
          </span>
          <span className="gk-caption">{formatRelativeTime(e.created_at)}</span>
        </li>
      ))}
    </ul>
  );
}

/** Rows that fit the panel without scrolling; "View all" has the rest. */
const AUDIT_ROWS = 5;

/** FR-AD7 / SD50: mail delivery and integration credentials (OAuth clients, notetaker apps,
 * hosted speech-to-text) are platform-wide, set in the operator console. Staff-only, so only
 * staff see this card; it is how they get there from an org's console instead of a hidden URL. */
function PlatformCard() {
  return (
    <Card size="sm" title="Platform">
      <Row label="Email delivery">
        <Link to="/ops/email" aria-label="Configure email delivery">
          Configure
        </Link>
      </Row>
      <Row label="Platform integrations">
        <Link to="/ops/integrations" aria-label="Configure platform integrations">
          Configure
        </Link>
      </Row>
      <span className="gk-caption">Set for all organizations in the operator console.</span>
    </Card>
  );
}

export default function OverviewPage() {
  const { org } = useOutletContext<AdminOutletContext>();
  const usage = useLoaded<OrgUsageOut>(`/orgs/${org.id}/usage`, "Couldn't load usage.");
  const quotas = useLoaded<OrgQuotaMetersOut>(`/orgs/${org.id}/quotas`, "Couldn't load quota usage.");
  const transcription = useLoaded<TranscriptionSettingsOut>(`/orgs/${org.id}/transcription`, "Couldn't load AI settings.");
  const ask = useLoaded<AskSettingsOut>(`/orgs/${org.id}/ask/settings`, "Couldn't load Ask settings.");
  const notesError = useLoaded<LastNotesErrorOut>(`/orgs/${org.id}/notes/last-error`, "");
  const audit = useLoaded<ListResponse<AuditEntryOut>>(`/orgs/${org.id}/audit-log`, "Couldn't load the audit log.");
  const loaded = usage && "data" in usage ? usage.data : null;
  const q = quotas && "data" in quotas ? quotas.data : null;
  const platformAdmin = useIsPlatformAdmin();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<"audit" | "limits">("audit");

  // The same rules as the AI and models Overview, so the two pages never disagree.
  const aiItems: AttentionItem[] =
    transcription && "data" in transcription
      ? aiFailureFlags(
          transcription.data,
          ask && "data" in ask ? ask.data : null,
          notesError && "data" in notesError ? notesError.data : null,
        ).map((f) => ({
          id: `ai-${f.id}`,
          kind: f.tone === "danger" ? "broken" : "near",
          message: f.message,
          to: f.to ?? `${adminPath(org.slug, "ai")}?tab=${f.tab}${f.slot === "diarization" ? "&slot=diarization" : ""}`,
          action: f.action,
        }))
      : [];
  const attention = sortAttention([...aiItems, ...(q ? quotaAttention(q) : [])]);

  function openLimits() {
    setPanel("limits");
    document.getElementById("admin-overview-panel")?.scrollIntoView?.({ block: "nearest" });
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Overview</h1>
          <p>{org.name} at a glance.</p>
        </div>
      </header>

      {attention.length ? (
        <div className="admin-attention" aria-label="Needs attention">
          {attention.map((item) => (
            <Callout
              key={item.id}
              tone={item.kind === "broken" ? "danger" : "now"}
              icon={item.kind === "broken" ? "warning-circle" : "warning"}
              className="admin-attention__item"
              data-kind={item.kind}
            >
              <span>{item.message}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => (item.to === "limits" ? openLimits() : navigate(item.to))}
              >
                {item.action ?? "View details"}
              </Button>
            </Callout>
          ))}
        </div>
      ) : null}

      {usage && "error" in usage ? <Failed state={usage} /> : null}
      <div className="admin-tiles">
        <Tile
          label="Members"
          value={q ? q.members.used : loaded ? loaded.member_count : "—"}
          meter={q?.members}
          show={count}
          caption={loaded ? `${loaded.team_count} teams` : undefined}
        />
        <Tile label="Recordings" value={loaded ? loaded.grackle_count : "—"} />
        <Tile
          label="Storage used"
          value={q ? formatBytes(q.storage_bytes.used) : loaded ? formatBytes(loaded.storage_bytes) : "—"}
          meter={q?.storage_bytes}
          show={formatBytes}
        />
        <Tile
          label="Public bandwidth"
          value={q ? formatBytes(q.public_egress_bytes_30d.used) : "—"}
          meter={q?.public_egress_bytes_30d}
          show={formatBytes}
          caption="Last 30 days"
        />
        <Tile
          label="Hosted AI minutes"
          value={q ? minutes(q.hosted_stt_seconds_30d.used) : "—"}
          meter={q?.hosted_stt_seconds_30d}
          show={minutes}
          caption="Last 30 days"
        />
      </div>

      <div className={`admin-overview${platformAdmin ? " admin-overview--3" : ""}`}>
        <SsoCard orgId={org.id} slug={org.slug} />
        <AiCard orgId={org.id} slug={org.slug} stt={transcription} />
        {platformAdmin ? <PlatformCard /> : null}
      </div>

      <Card size="sm" className="admin-overview__panel" id="admin-overview-panel">
        <Tabs
          variant="pill"
          items={[
            { value: "audit", label: "Recent audit events" },
            { value: "limits", label: "Limits" },
          ]}
          value={panel}
          onChange={(v) => setPanel(v as "audit" | "limits")}
          trailing={panel === "audit" ? <Link to={adminPath(org.slug, "audit-log")}>View all</Link> : null}
        />
        <div role="tabpanel">{panel === "audit" ? <AuditList audit={audit} /> : <LimitsDetail q={quotas} />}</div>
      </Card>
    </>
  );
}
